import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
	cupService,
	CupRequest,
	CupUpdateRequest,
	CupSearchParams,
} from "../services/cup.service";

// Re-export types for convenience
export type { CupRequest, CupUpdateRequest, CupSearchParams };

export const useCups = () => {
	return useQuery({
		queryKey: ["cups"],
		queryFn: cupService.getCups,
		staleTime: 5 * 60 * 1000, // 5 minutes
	});
};

/**
 * SIMS-8799 AC2 — vendors already in use. Cached like the rest: the list is small, changes only
 * when a cup is created with a new vendor, and the editor reads it on open rather than per
 * keystroke.
 */
export const useInUseVendors = () => {
	return useQuery({
		queryKey: ["cups", "vendors"],
		queryFn: cupService.getInUseVendors,
		staleTime: 5 * 60 * 1000,
	});
};

/**
 * SIMS-8850 — the dependency counts, fetched only while the confirmation is open.
 *
 * Scoped to the one cup deletion was asked for, not every row: the design closes over counts
 * already on the row, which would make every cups list load aggregate over the Enrollment
 * collection for N cups. Per-id follows the payer precedent and is cheap; the cost is a pending
 * state the design's dialog does not have, which ConfigDeleteDialog already provides.
 *
 * `retry: false` and `staleTime: 0` are deliberate — a stale or retried count is worse than a
 * stated failure, because the dialog blocks on "unknown" and offers Delete on "zero".
 */
export const useCupDependencies = (id: string | null, enabled: boolean) => {
	return useQuery({
		// NOT under the ["cups"] prefix. `useDeleteCup.onSuccess` invalidates ["cups"], which
		// prefix-matches — and since the dialog only closes after mutateAsync resolves, that fired
		// one GET /cups/<just-deleted-id>/dependencies against a record that no longer exists.
		queryKey: ["cup-dependencies", id],
		queryFn: () => cupService.getDependencies(id as string),
		enabled: enabled && Boolean(id),
		retry: false,
		staleTime: 0,
	});
};

export const useCupById = (id: string) => {
	return useQuery({
		queryKey: ["cup", id],
		queryFn: () => cupService.getCupById(id),
		enabled: !!id,
		staleTime: 5 * 60 * 1000,
	});
};

export const useSearchCups = (params: CupSearchParams) => {
	return useQuery({
		queryKey: ["cups", "search", params],
		queryFn: () => cupService.searchCups(params),
		enabled: Object.keys(params).length > 0,
		staleTime: 2 * 60 * 1000, // 2 minutes
	});
};

export const useCreateCup = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: CupRequest) => cupService.createCup(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["cups"] });
		},
	});
};

export const useUpdateCup = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: CupUpdateRequest) => cupService.updateCup(data),
		onSuccess: (data) => {
			queryClient.invalidateQueries({ queryKey: ["cups"] });
			queryClient.invalidateQueries({ queryKey: ["cup", data._id] });
		},
	});
};

export const useDeleteCup = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => cupService.deleteCup(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["cups"] });
		},
	});
};
